import { type WebWidgetModel } from '@web/types';
import {
    EButtonStyleType,
    EFontStyle,
    EHorizontalPosition,
    ELineStyle,
    ESize,
    InputBorderEnum,
    Widgets,
} from '@common/enums';
import { defaultWidgetParams, defaultWidgetPermission } from '@common/constants';
import { ProductCardViewComponent } from '@web/widgets/product-card/components/product-card-view/product-card-view.component';
import { ProductCardControlComponent } from '@web/widgets/product-card/components/product-card-control/product-card-control.component';
import {
    type ProductCardWidgetData,
    CardKindViewType,
    CardViewType,
    ViewModalType,
    ButtonActionType,
} from '@web/widgets/product-card/type';
import { type JssStyle } from 'jss';
import { type TranslateService } from '@ngx-translate/core';
import {
    DEFAULT_BUTTON_BASE,
    DEFAULT_BUTTON_HOVER_STATES,
} from '@web/widgets/common/base/base-button/base-button.const';
import { TRANSPARENT_VALUE } from '@components/sp-color-picker/src/constants/color-picker-presets.const';
import { AspectRatio, Attachment } from '@web/widgets/product-card/type/product-card-widget.type';

const productCardData = (translateService: TranslateService): ProductCardWidgetData => ({
    element: null,
    cardViewType: CardViewType.open,
    cardKindViewType: CardKindViewType.first,
    cardModalViewType: ViewModalType.vertical,
    basketButton: {
        label: translateService.instant('widget_product_basket_action_add'),
        styleType: EButtonStyleType.primary,
        size: ESize.M,
        action: ButtonActionType.basket,
        buttonBase: { ...DEFAULT_BUTTON_BASE },
        buttonHover: { ...DEFAULT_BUTTON_HOVER_STATES },
        isIndependentCorners: false,
        advancedRadius: {
            borderBottomLeftRadius: null,
            borderBottomRightRadius: null,
            borderTopLeftRadius: null,
            borderTopRightRadius: null,
        },
        isShadow: false,
        shadowSettings: null,
    },
    showMoreButton: {
        label: translateService.instant('widget_product_show_more_label'),
        styleType: EButtonStyleType.primary,
        size: ESize.M,
        action: ButtonActionType.basket,
        buttonBase: { ...DEFAULT_BUTTON_BASE },
        buttonHover: { ...DEFAULT_BUTTON_HOVER_STATES },
        isShowButton: true,
        isIndependentCorners: false,
        advancedRadius: {
            borderBottomLeftRadius: null,
            borderBottomRightRadius: null,
            borderTopLeftRadius: null,
            borderTopRightRadius: null,
        },
        isShadow: false,
        shadowSettings: null,
    },
    cardSettings: {
        general: {
            backgroundColor: TRANSPARENT_VALUE,
            borderRadius: 0,
            padding: 0,
            isBorder: null,
            borderWidth: 1,
            borderStyle: ELineStyle.SOLID,
            borderColor: '#000',
            galleryWidth: '60%',
            contentWidth: '40%',
        },
        gallery: {
            attachment: Attachment.contain,
            aspectRatio: AspectRatio.fourToThree,
            backgroundColor: '#eee',
            borderRadius: 0,
            margin: 0,
            buttonColor: '#000',
            buttonBackground: '#fff',
            paginationColor: '#000',
            changeSlide: false,
            fullByClick: false,
        },
        info: {
            backgroundColor: TRANSPARENT_VALUE,
            borderRadius: 0,
            isBorder: null,
            borderWidth: 1,
            borderStyle: ELineStyle.SOLID,
            borderColor: '#000',
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 40,
            paddingRight: 40,
        },
        typography: {
            titleSettings: {
                color: '#333',
                fontSize: 28,
                fontStyle: [EFontStyle.B],
            },
            descriptionSettings: {
                color: '#333',
                fontSize: 14,
                fontStyle: [],
            },
            priceSettings: {
                color: '#333',
                fontSize: 20,
                fontStyle: [],
            },
            oldPriceSettings: {
                color: '#c9c9d9',
                fontSize: 20,
                fontStyle: [],
            },
        },
        productVariants: {
            showLabels: true,
            textColor: '#333',
            inputBorderType: InputBorderEnum.FULL_BORDER,
            inputTextColor: '#333',
            backgroundColor: '#fff',
            borderColor: '#ddd',
            borderRadius: 3,
            inputSize: ESize.M,
        },
    },
    previewModalSettings: {
        general: {
            backgroundColor: TRANSPARENT_VALUE,
            borderRadius: 0,
            padding: 0,
            isBorder: null,
            borderWidth: 1,
            borderStyle: ELineStyle.SOLID,
            borderColor: '#000',
        },
        gallery: {
            attachment: Attachment.contain,
            aspectRatio: AspectRatio.default,
            backgroundColor: '#eee',
            borderRadius: 0,
        },
        info: {
            backgroundColor: TRANSPARENT_VALUE,
            borderRadius: 0,
            isBorder: null,
            borderWidth: 1,
            borderStyle: ELineStyle.SOLID,
            borderColor: '#000',
            paddingTop: 30,
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
        },
        typography: {
            titleSettings: {
                color: '#333',
                fontSize: 22,
                fontStyle: [EFontStyle.B],
                isShow: true,
            },
            descriptionSettings: {
                color: '#333',
                fontSize: 14,
                fontStyle: [],
                isShow: true,
            },
            priceSettings: {
                color: '#333',
                fontSize: 20,
                fontStyle: [EFontStyle.B],
                isShow: true,
            },
            oldPriceSettings: {
                color: '#c9c9d9',
                fontSize: 20,
                fontStyle: [],
                isShow: true,
            },
        },
    },
    modalSettings: {
        general: {
            backgroundColor: '#fff',
            borderRadius: 10,
            padding: 40,
            isBorder: null,
            borderWidth: 1,
            borderStyle: ELineStyle.SOLID,
            borderColor: '#333',
            substrateColor: 'rgba(0,0,0,0.3)',
        },
        typography: {
            titleSettings: {
                color: '#333',
                fontSize: 28,
                fontStyle: [EFontStyle.B],
            },
            descriptionSettings: {
                color: '#333',
                fontSize: 14,
                fontStyle: [],
            },
            priceSettings: {
                color: '#333',
                fontSize: 20,
                fontStyle: [EFontStyle.B],
            },
            oldPriceSettings: {
                color: '#c9c9d9',
                fontSize: 20,
                fontStyle: [],
            },
        },
        productVariants: {
            showLabels: true,
            textColor: '#333',
            inputBorderType: InputBorderEnum.FULL_BORDER,
            inputTextColor: '#333',
            backgroundColor: '#fff',
            borderColor: '#ddd',
            borderRadius: 3,
            inputSize: ESize.M,
        },
    },
    isShowCardSettings: false,
    previewHorizontalPosition: EHorizontalPosition.L,
    selectedFromCatalog: false,
});

const productCardStyles: JssStyle = {
    maxWidth: '100%',
    margin: '0 auto',
    display: 'block',
};
export const productCardWidgetModel: WebWidgetModel = {
    componentView: ProductCardViewComponent,
    componentControl: ProductCardControlComponent,
    name: Widgets.PRODUCT_CARD,
    label: 'widget_product_card_label',
    data: productCardData,
    styles: productCardStyles,
    permission: defaultWidgetPermission,
    params: defaultWidgetParams,
};
